import { cva } from "class-variance-authority";
import { Slot } from "radix-ui";
import * as React from "react";
import { cn } from "src/lib/utils";
import type { VariantProps } from "class-variance-authority";

export const variants = ["primary", "secondary", "tertiary", "ghost"] as const;

type ButtonVariant = (typeof variants)[number];

const buttonVariants = cva(
	[
		"min-w-20 p-3 inline-flex items-center justify-center rounded-md border-2 font-semibold disabled:opacity-50",
		"focus:ring-3 focus:ring-havelock-blue-500/50 focus:ring-offset-5 focus:ring-offset-white focus:outline-none",
	],
	{
		variants: {
			variant: {
				primary:
					"bg-lima-500 text-endeavour-600 border-endeavour-600 hover:bg-lima-600 hover:border-lima-600",
				secondary: "text-endeavour-500 border-endeavour-500 hover:text-endeavour-600 hover:border-endeavour-600",
				tertiary: "bg-transparent border-0 text-endeavour-900 hover:text-endeavour-600",
				ghost: "bg-transparent border-0 text-endeavour-500 hover:text-endeavour-600",
			} satisfies Record<ButtonVariant, string>,
			size: {
				sm: "p-2 text-xs leading-4",
				base: "text-base leading-5",
				lg: "text-lg leading-6",
			},
			state: {
				default: "",
				inverted: "bg-white border-white",
				destructive: "bg-lipstick-500 text-white border-lipstick-500",
			},
		},
		compoundVariants: [
			{
				variant: "primary",
				state: "inverted",
				className: "text-endeavour-500 hover:bg-endeavour-50 hover:border-endeavour-50",
			},
			{
				variant: "primary",
				state: "destructive",
				className: "hover:bg-lipstick-600 hover:border-lipstick-600 focus:outline-lipstick-500/60",
			},
			{
				variant: "secondary",
				state: "inverted",
				className: "bg-transparent text-white hover:text-endeavour-50 hover:border-endeavour-50",
			},
			{
				variant: "secondary",
				state: "destructive",
				className: "bg-transparent text-lipstick-500 hover:text-lipstick-600 hover:border-lipstick-600 focus:outline-lipstick-500/60",
			},
			{
				variant: "tertiary",
				state: "inverted",
				className: "bg-transparent text-white hover:text-endeavour-50",
			},
			{
				variant: "tertiary",
				state: "destructive",
				className: "bg-transparent text-lipstick-500 hover:text-lipstick-600 focus:outline-lipstick-500/60",
			},
			{
				variant: "ghost",
				state: "inverted",
				className: "bg-transparent text-white hover:text-endeavour-50",
			},
			{
				variant: "ghost",
				state: "destructive",
				className: "bg-transparent text-lipstick-500 hover:text-lipstick-600 focus:outline-lipstick-500/60",
			},
		],
		defaultVariants: {
			variant: "primary",
			size: "base",
			state: undefined,
		},
	},
);

export type ButtonProps = {
	asChild?: boolean;
	leftIcon?: React.ReactNode;
	rightIcon?: React.ReactNode;
} & React.ComponentProps<"button"> &
	VariantProps<typeof buttonVariants>;

const Button = ({
	className,
	children,
	asChild = false,
	leftIcon,
	rightIcon,
	variant = "primary",
	size = "base",
	state,
	...props
}: ButtonProps) => {
	const Comp = asChild ? Slot.Root : "button";

	return (
		<Comp
			type="button"
			className={cn(
				buttonVariants({
					variant,
					size,
					state,
					className,
				}),
			)}
			{...props}
		>
			<div>
				{leftIcon}
				<span className="px-2">{children}</span>
				{rightIcon}
			</div>
		</Comp>
	);
};

export { Button };

// 🔬 TBD
